import React, { useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';

const AIDisclaimerPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const lastUpdated = "January 24, 2026";

  // Scroll to section if hash is present
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location.hash]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const sections = [
    { id: 'purpose', title: '1. Purpose of AI tools' },
    { id: 'no-automated', title: '2. No automated decision-making' },
    { id: 'ai-augmented', title: '3. AI-augmented consultants' },
    { id: 'data-used', title: '4. Data used by AI' },
    { id: 'liability', title: '5. Limitation of liability – AI' },
    { id: 'user-responsibility', title: '6. User responsibility' },
    { id: 'evolution', title: '7. Evolution of AI features' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/')} className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back to Home</span>
          </Button>
          <div className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-blue-600" />
            <span className="font-semibold text-gray-800">AI Disclaimer</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-8 md:py-12">
        <div className="lg:flex lg:gap-8">
          {/* Sidebar Table of Contents - Desktop */}
          <aside className="hidden lg:block lg:w-64 flex-shrink-0">
            <div className="sticky top-24 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-800 mb-4 text-sm uppercase tracking-wide">Table of Contents</h3>
              <nav className="space-y-1">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className="block w-full text-left px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-10">
            {/* Title */}
            <div className="text-center mb-10 pb-8 border-b border-gray-200">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <Bot className="w-8 h-8 text-blue-600" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">AI Disclaimer & AI-Augmented Consultants</h1>
              <p className="text-gray-500">Last updated: {lastUpdated}</p>
            </div>

            {/* Mobile Table of Contents */}
            <div className="lg:hidden mb-8">
              <details className="bg-gray-50 rounded-lg border border-gray-200">
                <summary className="px-4 py-3 cursor-pointer font-semibold text-gray-800">
                  Table of Contents
                </summary>
                <nav className="px-4 pb-4 space-y-1">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className="block w-full text-left px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      {section.title}
                    </button>
                  ))}
                </nav>
              </details>
            </div>

            {/* Introduction */}
            <section className="mb-12">
              <p className="text-gray-700 leading-relaxed">
                This AI Disclaimer supplements and forms an integral part of the <Link to="/terms" className="text-blue-600 hover:underline">Terms of Use</Link> and should be read together with the <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>.
              </p>
            </section>

            {/* 1. Purpose of AI tools */}
            <section id="purpose" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Purpose of AI tools</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>SnowExchange uses artificial intelligence tools to assist Users with:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>matching and recommendations,</li>
                  <li>prioritisation and visibility of profiles,</li>
                  <li>qualitative indicators (e.g. relevance or fit).</li>
                </ul>
              </div>
            </section>

            {/* 2. No automated decision-making */}
            <section id="no-automated" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">2. No automated decision-making</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
                  <p>
                    AI tools do not make automated decisions producing legal or contractual effects. Human review and judgment are always required.
                  </p>
                </div>
              </div>
            </section>

            {/* 3. AI-augmented consultants */}
            <section id="ai-augmented" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">3. AI-augmented consultants</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  "AI-augmented" refers to the use of productivity or assistance tools by consultants. It does not constitute:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>certification,</li>
                  <li>validation of competence,</li>
                  <li>guarantee of performance or compliance.</li>
                </ul>
                <p className="font-medium text-gray-900 mt-4">
                  SnowExchange does not verify actual AI usage or effectiveness.
                </p>
              </div>
            </section>

            {/* 4. Data used by AI */}
            <section id="data-used" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Data used by AI</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  AI tools rely on data provided by Users ("as declared"). SnowExchange does not guarantee accuracy or completeness.
                </p>
              </div>
            </section>

            {/* 5. Limitation of liability – AI */}
            <section id="liability" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                5. Limitation of liability – AI
                <span className="ml-2 px-2 py-1 bg-red-100 text-red-700 text-xs font-bold rounded uppercase">Critical</span>
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <div className="bg-red-50 border border-red-200 rounded-lg p-5">
                  <p className="mb-3">SnowExchange Parties are not liable for:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>recommendation errors,</li>
                    <li>algorithmic bias,</li>
                    <li>inaccurate or incomplete data,</li>
                    <li>decisions taken based on AI outputs,</li>
                    <li>resulting business or operational consequences.</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 6. User responsibility */}
            <section id="user-responsibility" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">6. User responsibility</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>Users remain solely responsible for:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>decisions and selections,</li>
                  <li>due diligence and validation,</li>
                  <li>contractual and regulatory compliance.</li>
                </ul>
              </div>
            </section>

            {/* 7. Evolution of AI features */}
            <section id="evolution" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Evolution of AI features</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  AI features may be modified, improved, or discontinued at any time without obligation of continuity.
                </p>
              </div>
            </section>

            {/* Related Documents */}
            <div className="border-t border-gray-200 pt-8 mt-12">
              <h3 className="font-semibold text-gray-800 mb-4">Related Documents</h3>
              <div className="flex flex-wrap gap-4">
                <Link to="/terms" className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 transition-colors">
                  Terms of Use
                </Link>
                <Link to="/privacy" className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 transition-colors">
                  Privacy Policy
                </Link>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 pt-8 mt-8 text-center">
              <p className="text-sm text-gray-500">
                © {new Date().getFullYear()} SnowExchange. All rights reserved.
              </p>
              <p className="text-sm text-gray-400 mt-2">
                Last updated: {lastUpdated}
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AIDisclaimerPage;
